import React, { useEffect, useState } from 'react'
import PageHeader from '../../../PageHeader/PageHeader'
import { HitApi } from '../../../Api/ApiHit'
import { searchBill, searchStaffGeneral } from '../../../constant/Constant'
import { getSelectedUnit, getElevateUser } from '../../../storage/Storage'
import SearchableSelect from '../../../components/AppSelect/SearchableSelect'
import { useDispatch, useSelector } from 'react-redux'
import { getErrorMsg, hasError } from '../../../utils/utils'
import AppTable from '../../../components/AppTable/AppTable'
import { setPagination } from '../../../redux/Actions/TableAction'
import toast from 'react-hot-toast'
import DateSearch from '../../../components/DateSearch/DateSearch'
import { exportToCSV, exportToPDF } from '../../../utils/exportUtils'
import ViewButton from '../../../components/AppButton/ViewButton'
import CustomModal from '../../../components/CustomModal/CustumModal'
import { XMarkIcon } from '@heroicons/react/24/outline'

function StaffReportMaster() {
    const ApiReducer = useSelector((state) => state.ApiReducer);
    const TableDataReducer = useSelector((state) => state.TableReducer);
    const dispatch = useDispatch();

    const [userOptions, setUserOptions] = useState([]);
    const [usersData, setUsersData] = useState([]);
    const [billsData, setBillsData] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [selectedStaffId, setSelectedStaffId] = useState('');
    const [selectedStaffName, setSelectedStaffName] = useState('');
    const [selectedStaffTarget, setSelectedStaffTarget] = useState(0);
    const [selectedStaffSalary, setSelectedStaffSalary] = useState(0);

    // Helper to extract staff ID from staff field (can be object or string)
    const getStaffId = (staff) => {
        if (!staff) return null;
        return typeof staff === 'object' ? staff._id : staff;
    };

    // Check if current user is SuperAdmin
    const currentUser = getElevateUser();
    const isSuperAdmin = currentUser?.roleData?.roleType === 'SuperAdmin' && currentUser?.userType === 'SuperAdmin';

    // Search state
    const [activeDateRange, setActiveDateRange] = useState(null);
    const [searchMode, setSearchMode] = useState('single'); // 'single' or 'range' - Single Date by default
    const [singleDate, setSingleDate] = useState(new Date().toISOString().split('T')[0]); // Today's date by default
    const [initialLoadDone, setInitialLoadDone] = useState(false);

    // Modal state
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedBill, setSelectedBill] = useState(null);
    const [staffDetailsMap, setStaffDetailsMap] = useState({});

    useEffect(() => {
        getUsers()

        // If not SuperAdmin, automatically select current user
        if (!isSuperAdmin && currentUser?._id) {
            setSelectedStaffId(currentUser._id);
            setSelectedStaffName(currentUser.name || currentUser.username || 'Current User');

            // Find and set target and salary for current user
            const user = usersData.find(u => u._id === currentUser._id);
            setSelectedStaffTarget(user?.target || 0);
            setSelectedStaffSalary(user?.salary || 0);

            // Auto-load will be triggered by the other useEffect
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    useEffect(() => {
        // Update selectedStaffId when ApiReducer changes (only for SuperAdmin)
        if (isSuperAdmin && ApiReducer?.apiJson?.userId) {
            const newStaffId = ApiReducer.apiJson.userId;

            // If staff changed, reset search states
            if (newStaffId !== selectedStaffId) {
                setActiveDateRange(null);
                setSingleDate(new Date().toISOString().split('T')[0]); // Reset to today's date
                setBillsData([]);
                setInitialLoadDone(false); // Reset initial load flag for new staff
            }

            setSelectedStaffId(newStaffId);
            const selectedOption = userOptions.find(opt => opt.value === newStaffId);
            setSelectedStaffName(selectedOption?.label || '');

            // Set the selected staff's monthly target and salary
            const selectedUser = usersData.find(user => user._id === newStaffId);
            setSelectedStaffTarget(selectedUser?.target || 0);
            setSelectedStaffSalary(selectedUser?.salary || 0);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ApiReducer?.apiJson?.userId, userOptions, usersData])

    useEffect(() => {
        if (selectedStaffId && searchMode === 'range' && activeDateRange) {
            performDateRangeSearch(activeDateRange.startDate, activeDateRange.endDate);
        } else if (selectedStaffId && searchMode === 'single' && singleDate) {
            performSingleDateSearch(singleDate);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedStaffId, activeDateRange, singleDate, searchMode])

    // Auto-load data on initial load with today's date
    useEffect(() => {
        if (selectedStaffId && !initialLoadDone && searchMode === 'single' && singleDate) {
            performSingleDateSearch(singleDate);
            setInitialLoadDone(true);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedStaffId])

    // Fetch staff details when bill is selected
    useEffect(() => {
        if (!selectedBill) return;

        const fetchStaffDetails = async () => {
            const staffIds = new Set();

            // Collect staff IDs from services
            if (selectedBill.services && Array.isArray(selectedBill.services)) {
                selectedBill.services.forEach(service => {
                    if (service.staff) {
                        const staffId = typeof service.staff === 'object' ? service.staff._id : service.staff;
                        if (staffId) staffIds.add(staffId);
                    }
                });
            }

            // Collect staff IDs from products
            if (selectedBill.products && Array.isArray(selectedBill.products)) {
                selectedBill.products.forEach(product => {
                    if (product.staff) {
                        const staffId = typeof product.staff === 'object' ? product.staff._id : product.staff;
                        if (staffId) staffIds.add(staffId);
                    }
                });
            }

            // Fetch details for each staff member
            const staffDetailsTemp = {};

            for (const staffId of staffIds) {
                try {
                    const json = {
                        page: 1,
                        limit: 1,
                        search: { _id: staffId }
                    };

                    const res = await HitApi(json, searchStaffGeneral);

                    if (res?.statusCode === 200 && res?.data?.docs?.[0]) {
                        staffDetailsTemp[staffId] = res.data.docs[0];
                    }
                } catch (error) {
                    console.error(`Error fetching staff details for ID ${staffId}:`, error);
                }
            }

            setStaffDetailsMap(staffDetailsTemp);
        };

        fetchStaffDetails();
    }, [selectedBill]);

    const getUsers = () => {
        const json = {
            page: 1,
            limit: 100,
            search: {
                unitIds: getSelectedUnit()._id
            }
        }
        HitApi(json, searchStaffGeneral).then((res) => {
            if (res?.statusCode === 200 && res?.data?.docs) {
                setUsersData(res.data.docs);
                const options = res.data.docs.map((user) => ({
                    label: `${user.name} (${user.username})`,
                    value: user._id
                }));
                setUserOptions(options);

                // If not SuperAdmin, set current user's target and salary after data loads
                if (!isSuperAdmin && currentUser?._id) {
                    const user = res.data.docs.find(u => u._id === currentUser._id);
                    if (user) {
                        setSelectedStaffTarget(user?.target || 0);
                        setSelectedStaffSalary(user?.salary || 0);
                    }
                }
            }
        })
    }

    const handleClearSearch = () => {
        setActiveDateRange(null);
        if (searchMode === 'single') {
            setSingleDate(new Date().toISOString().split('T')[0]); // Reset to today's date
        } else {
            setSingleDate('');
        }
        setBillsData([]);
        dispatch(setPagination({ total: 0, page: 1, limit: 1000 }));
    };

    // Single date search
    const performSingleDateSearch = (date) => {
        if (!selectedStaffId) return;

        setIsLoading(true);

        const startTimestamp = new Date(date).setHours(0, 0, 0, 0);
        const endTimestamp = new Date(date).setHours(23, 59, 59, 999);

        const searchObj = {
            unitId: getSelectedUnit()._id,
            status: 'completed',
            $or: [
                { "services.staff": selectedStaffId },
                { "products.staff": selectedStaffId }
            ],
            createdAt: {
                $gte: startTimestamp,
                $lte: endTimestamp
            }
        };

        const json = {
            page: 1,
            limit: 1000,
            search: searchObj
        };

        HitApi(json, searchBill).then((res) => {
            if (res?.statusCode === 200) {
                setBillsData(res.data.docs || []);
                const paginationData = {
                    total: res?.data?.totalDocs || 0,
                    page: 1,
                    limit: 1000
                };
                dispatch(setPagination(paginationData));
                toast.success(`Found ${res?.data?.totalDocs || 0} bills on ${new Date(date).toLocaleDateString()}`);
            } else {
                setBillsData([]);
                toast.error('Failed to fetch bills. Please try again.');
            }
        }).catch(() => {
            setBillsData([]);
            toast.error('Error fetching bills. Please try again.');
        }).finally(() => {
            setIsLoading(false);
        });
    };

    // Date range search
    const performDateRangeSearch = (startDate, endDate) => {
        if (!selectedStaffId) return;

        setIsLoading(true);

        const startTimestamp = new Date(startDate).setHours(0, 0, 0, 0);
        const endTimestamp = new Date(endDate).setHours(23, 59, 59, 999);

        const searchObj = {
            unitId: getSelectedUnit()._id,
            status: 'completed',
            $or: [
                { "services.staff": selectedStaffId },
                { "products.staff": selectedStaffId }
            ],
            createdAt: {
                $gte: startTimestamp,
                $lte: endTimestamp
            }
        };

        const json = {
            page: 1,
            limit: 1000,
            search: searchObj
        };

        HitApi(json, searchBill).then((res) => {
            if (res?.statusCode === 200) {
                setBillsData(res.data.docs || []);
                const paginationData = {
                    total: res?.data?.totalDocs || 0,
                    page: 1,
                    limit: 1000
                };
                dispatch(setPagination(paginationData));
                toast.success(`Found ${res?.data?.totalDocs || 0} bills in the selected date range`);
            } else {
                setBillsData([]);
                toast.error('Failed to fetch bills. Please try again.');
            }
        }).catch(() => {
            setBillsData([]);
            toast.error('Error fetching bills. Please try again.');
        }).finally(() => {
            setIsLoading(false);
        });
    };

    const handleSingleDateSearch = () => {
        if (!selectedStaffId) {
            toast.error('Please select a staff member first');
            return;
        }

        if (!singleDate) {
            toast.error('Please select a date');
            return;
        }

        performSingleDateSearch(singleDate);
    };

    const handleDateRangeSearch = (startDate, endDate) => {
        if (!selectedStaffId) {
            toast.error('Please select a staff member first');
            return;
        }

        // Calculate days in range
        const start = new Date(startDate);
        const end = new Date(endDate);
        const daysInRange = Math.ceil((end - start) / (1000 * 60 * 60 * 24)) + 1;

        // Check if range exceeds 90 days
        if (daysInRange > 90) {
            toast.error('You cannot fetch data for more than 90 days (3 months)');
            return;
        }

        setActiveDateRange({ startDate, endDate });
    };

    const handleViewBill = (billData) => {
        setSelectedBill(billData);
        setIsModalOpen(true);
    };

    const handleCloseModal = () => {
        setIsModalOpen(false);
        setSelectedBill(null);
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric'
        });
    };

    const formatTime = (dateString) => {
        if (!dateString) return 'N/A';
        const date = new Date(dateString);
        return date.toLocaleTimeString('en-US', {
            hour: '2-digit',
            minute: '2-digit',
            hour12: true
        });
    };

    const formatCurrency = (amount) => {
        if (amount === null || amount === undefined) return '₹0';
        return `₹${Number(amount).toFixed(2)}`;
    };

    const getOrdinalSuffix = (day) => {
        if (day > 3 && day < 21) return 'th';
        switch (day % 10) {
            case 1: return 'st';
            case 2: return 'nd';
            case 3: return 'rd';
            default: return 'th';
        }
    };

    const calculateStaffIncentive = (services, staffId, isWalletPayment = false) => {
        if (!services || !Array.isArray(services)) return 0;

        let totalIncentive = 0;
        services.forEach(service => {
            if (getStaffId(service.staff) === staffId && service.incentive) {
                let serviceTotal = service.pricing?.finalPrice || service.pricing?.totalPrice || 0;
                if (isWalletPayment) {
                    serviceTotal = serviceTotal * 0.70;
                }
                const incentiveAmount = (serviceTotal * service.incentive) / 100;
                totalIncentive += incentiveAmount;
            }
        });

        return totalIncentive;
    };

    const calculateProductIncentive = (products, staffId, isWalletPayment = false) => {
        if (!products || !Array.isArray(products)) return 0;

        let totalIncentive = 0;
        products.forEach(product => {
            if (getStaffId(product.staff) === staffId) {
                let productTotal = product.pricing?.finalPrice || product.pricing?.totalPrice || 0;
                if (isWalletPayment) {
                    productTotal = productTotal * 0.70;
                }
                // Use product's incentive if available, otherwise default to 5%
                const incentivePercentage = product.incentive || 5;
                const incentiveAmount = (productTotal * incentivePercentage) / 100;
                totalIncentive += incentiveAmount;
            }
        });

        return totalIncentive;
    };

    const renderBillStatus = (status) => {
        const statusText = status || 'pending';
        let bgColor = 'bg-gray-100';
        let textColor = 'text-gray-800';

        if (statusText === 'completed') {
            bgColor = 'bg-green-100';
            textColor = 'text-green-800';
        } else if (statusText === 'cancelled') {
            bgColor = 'bg-red-100';
            textColor = 'text-red-800';
        }

        return (
            <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${bgColor} ${textColor}`}>
                {statusText.charAt(0).toUpperCase() + statusText.slice(1)}
            </span>
        );
    };

    const renderPaymentStatus = (payment) => {
        const status = payment?.paymentStatus || 'Pending';
        let bgColor = 'bg-yellow-100';
        let textColor = 'text-yellow-800';

        if (status === 'Paid') {
            bgColor = 'bg-green-100';
            textColor = 'text-green-800';
        } else if (status === 'Partial') {
            bgColor = 'bg-orange-100';
            textColor = 'text-orange-800';
        } else if (status === 'Unpaid') {
            bgColor = 'bg-red-100';
            textColor = 'text-red-800';
        }

        return (
            <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${bgColor} ${textColor}`}>
                {status}
            </span>
        );
    };

    // Calculate target achievement percentage
    const calculateTargetAchievement = (totalRevenue) => {
        if (!selectedStaffTarget || selectedStaffTarget === 0) {
            return {
                percentage: 0,
                target: 0,
                achieved: totalRevenue,
                perDayTarget: 0,
                daysInRange: 0
            };
        }

        // Calculate per day target (monthly target / 30)
        const perDayTarget = selectedStaffTarget / 30;

        let proportionalTarget = selectedStaffTarget;
        let daysInRange = 30;

        // If single date is selected, use 1 day
        if (searchMode === 'single' && singleDate) {
            daysInRange = 1;
            proportionalTarget = perDayTarget * daysInRange;
        }
        // If date range is selected, calculate proportional target
        else if (searchMode === 'range' && activeDateRange) {
            const startDate = new Date(activeDateRange.startDate);
            const endDate = new Date(activeDateRange.endDate);
            daysInRange = Math.ceil((endDate - startDate) / (1000 * 60 * 60 * 24)) + 1;

            // Calculate proportional target = per day target × days in range
            proportionalTarget = perDayTarget * daysInRange;
        }

        // Calculate achievement percentage
        const achievementPercentage = (totalRevenue / proportionalTarget) * 100;

        return {
            percentage: achievementPercentage,
            target: proportionalTarget,
            achieved: totalRevenue,
            perDayTarget: perDayTarget,
            daysInRange: daysInRange
        };
    };

    // Calculate proportional salary based on selected days
    const calculateProportionalSalary = () => {
        if (!selectedStaffSalary || selectedStaffSalary === 0) {
            return {
                monthlySalary: 0,
                proportionalSalary: 0,
                daysInRange: 0,
                monthName: ''
            };
        }

        // Calculate per day salary (monthly salary / 30)
        const perDaySalary = selectedStaffSalary / 30;

        let daysInRange = 30;
        let proportionalSalary = selectedStaffSalary;
        let monthName = '';

        // If single date is selected, use the day of the month (e.g., 11 for 11th Dec)
        if (searchMode === 'single' && singleDate) {
            const selectedDate = new Date(singleDate);
            daysInRange = selectedDate.getDate(); // Get day of month (1-31)
            proportionalSalary = perDaySalary * daysInRange;
            monthName = selectedDate.toLocaleDateString('en-US', { month: 'short' }); // Get month name (e.g., "Dec")
        }
        // If date range is selected, calculate proportional salary
        else if (searchMode === 'range' && activeDateRange) {
            const startDate = new Date(activeDateRange.startDate);
            const endDate = new Date(activeDateRange.endDate);
            daysInRange = Math.ceil((endDate - startDate) / (1000 * 60 * 60 * 24)) + 1;

            // Calculate proportional salary = per day salary × days in range
            proportionalSalary = perDaySalary * daysInRange;
        }

        return {
            monthlySalary: selectedStaffSalary,
            proportionalSalary: proportionalSalary,
            daysInRange: daysInRange,
            monthName: monthName
        };
    };

    // Calculate summary statistics
    const calculateSummary = () => {
        if (!billsData || billsData.length === 0) return null;

        let totalBills = billsData.length;
        let totalRevenue = 0;
        let serviceRevenue = 0;
        let productRevenue = 0;
        let totalServices = 0;
        let totalProducts = 0;
        let totalServiceIncentive = 0;
        let totalProductIncentive = 0;

        billsData.forEach(bill => {
            // Check if payment is via wallet
            const isWalletPayment = bill.payment?.activePaymentMethods?.some(
                method => method.method?.toLowerCase() === 'wallet'
            ) || false;

            // Calculate revenue from services performed by selected staff ONLY
            if (bill.services && Array.isArray(bill.services)) {
                bill.services.forEach(service => {
                    if (getStaffId(service.staff) === selectedStaffId) {
                        // Add revenue from this service
                        let serviceTotalPrice = service.pricing?.totalPrice || (service.pricing?.finalPrice * (service.quantity || 1)) || 0;

                        // Deduct 30% if payment is via wallet
                        if (isWalletPayment) {
                            serviceTotalPrice = serviceTotalPrice * 0.70; // 70% of original amount
                        }

                        totalRevenue += serviceTotalPrice;
                        serviceRevenue += serviceTotalPrice;

                        // Count services
                        totalServices += service.quantity || 1;
                    }
                });
            }

            // Calculate revenue from products sold by selected staff
            if (bill.products && Array.isArray(bill.products)) {
                bill.products.forEach(product => {
                    if (getStaffId(product.staff) === selectedStaffId) {
                        let productTotalPrice = product.pricing?.totalPrice || (product.pricing?.finalPrice * (product.quantity || 1)) || 0;

                        // Deduct 30% if payment is via wallet
                        if (isWalletPayment) {
                            productTotalPrice = productTotalPrice * 0.70; // 70% of original amount
                        }

                        productRevenue += productTotalPrice;

                        totalProducts += product.quantity || 1;
                    }
                });
            }

            // NOTE: Products are NOT included in revenue calculation for target achievement
            // Products sold by staff do NOT count towards their revenue target

            // Calculate incentives using the helper functions (apply wallet discount if applicable)
            totalServiceIncentive += calculateStaffIncentive(bill.services, selectedStaffId, isWalletPayment);
            totalProductIncentive += calculateProductIncentive(bill.products, selectedStaffId, isWalletPayment);
        });

        const totalIncentive = totalServiceIncentive + totalProductIncentive;
        const targetData = calculateTargetAchievement(totalRevenue);
        const salaryData = calculateProportionalSalary();

        return {
            totalBills,
            totalRevenue,
            serviceRevenue,
            productRevenue,
            totalServices,
            totalProducts,
            totalIncentive,
            totalServiceIncentive,
            totalProductIncentive,
            targetAchievement: targetData,
            salaryInfo: salaryData
        };
    };

    const summary = calculateSummary();

    // Export functionality
    const handleExportData = async (format) => {
        if (!selectedStaffId) {
            toast.error('Please select a staff member first');
            return;
        }

        try {
            toast.loading('Preparing export...');

            let searchObj = {
                unitId: getSelectedUnit()._id,
                status: 'completed',
                $or: [
                    { "services.staff": selectedStaffId },
                    { "products.staff": selectedStaffId }
                ]
            };

            if (searchMode === 'single' && singleDate) {
                const startTimestamp = new Date(singleDate).setHours(0, 0, 0, 0);
                const endTimestamp = new Date(singleDate).setHours(23, 59, 59, 999);
                searchObj.createdAt = {
                    $gte: startTimestamp,
                    $lte: endTimestamp
                };
            } else if (searchMode === 'range' && activeDateRange) {
                const startTimestamp = new Date(activeDateRange.startDate).setHours(0, 0, 0, 0);
                const endTimestamp = new Date(activeDateRange.endDate).setHours(23, 59, 59, 999);
                searchObj.createdAt = {
                    $gte: startTimestamp,
                    $lte: endTimestamp
                };
            } else {
                toast.dismiss();
                toast.error('Please select a date or date range first');
                return;
            }

            const json = {
                page: 1,
                limit: 10000,
                search: searchObj
            };

            const res = await HitApi(json, searchBill);

            if (res?.statusCode === 200 && res?.data?.docs) {
                const allData = res.data.docs;

                if (allData.length === 0) {
                    toast.dismiss();
                    toast.error('No data to export');
                    return;
                }

                const exportHeaders = [
                    { title: 'S.No', key: 'serialNumber' },
                    { title: 'Bill Number', key: 'billNumber' },
                    { title: 'Transaction ID', key: 'transactionId' },
                    { title: 'Date', key: 'date' },
                    { title: 'Time', key: 'time' },
                    { title: 'Client Name', key: 'clientName' },
                    { title: 'Phone Number', key: 'phoneNumber' },
                    { title: 'Services Count', key: 'servicesCount' },
                    { title: 'Products Count', key: 'productsCount' },
                    { title: 'Service Incentive', key: 'serviceIncentive' },
                    { title: 'Product Incentive', key: 'productIncentive' },
                    { title: 'Total Incentive', key: 'totalIncentive' },
                    { title: 'Bill Amount', key: 'billAmount' },
                    { title: 'Bill Status', key: 'status' }
                ];

                const flattenedData = allData.map((row, index) => {
                    const staffServices = row.services?.filter(s => getStaffId(s.staff) === selectedStaffId) || [];
                    const staffProducts = row.products?.filter(p => getStaffId(p.staff) === selectedStaffId) || [];
                    const servicesQuantity = staffServices.reduce((sum, s) => sum + (s.quantity || 1), 0);
                    const productsQuantity = staffProducts.reduce((sum, p) => sum + (p.quantity || 1), 0);
                    const serviceIncentive = calculateStaffIncentive(row.services, selectedStaffId);
                    const productIncentive = calculateProductIncentive(row.products, selectedStaffId);

                    return {
                        serialNumber: index + 1,
                        billNumber: row.billNumber || 'N/A',
                        transactionId: row.transactionId || 'N/A',
                        date: formatDate(row.timestamp),
                        time: formatTime(row.timestamp),
                        clientName: row.client?.name || 'N/A',
                        phoneNumber: row.client?.phoneNumber || 'N/A',
                        servicesCount: servicesQuantity,
                        productsCount: productsQuantity,
                        serviceIncentive: serviceIncentive.toFixed(2),
                        productIncentive: productIncentive.toFixed(2),
                        totalIncentive: (serviceIncentive + productIncentive).toFixed(2),
                        billAmount: Number(row.calculations?.totals?.finalAmount || 0).toFixed(2),
                        status: row.status ? row.status.charAt(0).toUpperCase() + row.status.slice(1) : 'Pending'
                    };
                });

                const filename = `staff_report_${selectedStaffName.replace(/\s+/g, '_')}_${new Date().toISOString().split('T')[0]}`;

                toast.dismiss();

                if (format === 'csv') {
                    exportToCSV(flattenedData, exportHeaders, `${filename}.csv`);
                    toast.success(`Exported ${allData.length} bills to CSV`);
                } else if (format === 'pdf') {
                    exportToPDF(flattenedData, exportHeaders, `${filename}.pdf`, `Staff Report - ${selectedStaffName}`);
                    toast.success(`Exported ${allData.length} bills to PDF`);
                }
            } else {
                toast.dismiss();
                toast.error('Failed to fetch data for export');
            }
        } catch (error) {
            toast.dismiss();
            toast.error('Error exporting data');
            console.error('Export error:', error);
        }
    };

    const baseIndex = (TableDataReducer.pagination.page - 1) * TableDataReducer.pagination.limit;

    const tableHeaders = [
        {
            title: "S.No",
            key: "serialNumber",
            width: "60px",
            align: "center",
            render: (_, __, index) => {
                const serialNumber = baseIndex + index + 1;
                return (
                    <div className="text-center">
                        <span className="text-sm font-medium text-gray-900">{serialNumber}</span>
                    </div>
                );
            }
        },
        {
            title: "Bill Details",
            key: "billNumber",
            render: (value, row) => (
                <div>
                    <div className="font-medium text-gray-900 text-sm">{value || 'N/A'}</div>
                    <div className="text-xs text-gray-500">
                        {row.transactionId || 'No transaction ID'}
                    </div>
                    <div className="text-xs text-gray-400 mt-1">
                        {formatDate(row.timestamp)} • {formatTime(row.timestamp)}
                    </div>
                </div>
            )
        },
        {
            title: "Client",
            key: "client",
            render: (client) => (
                <div>
                    <div className="font-medium text-gray-900 text-sm">
                        {client?.name || 'N/A'}
                    </div>
                    <div className="text-xs text-gray-500">
                        {client?.phoneNumber || 'N/A'}
                    </div>
                </div>
            )
        },
        {
            title: "Services",
            key: "services",
            align: "left",
            render: (services, row) => {
                const staffServices = services?.filter(s => getStaffId(s.staff) === selectedStaffId) || [];
                const totalQuantity = staffServices.reduce((sum, s) => sum + (s.quantity || 1), 0);
                const serviceIncentive = calculateStaffIncentive(services, selectedStaffId);

                return (
                    <div>
                        <div className="font-medium text-blue-600 text-sm mb-1">
                            {totalQuantity} Service{totalQuantity !== 1 ? 's' : ''}
                        </div>
                        {staffServices.length > 0 ? (
                            <>
                                <div className="space-y-0.5">
                                    {staffServices.slice(0, 3).map((service, index) => (
                                        <div key={index} className="text-xs text-gray-700">
                                            • {service.name} {service.quantity > 1 ? `(x${service.quantity})` : ''}
                                        </div>
                                    ))}
                                    {staffServices.length > 3 && (
                                        <div className="text-xs text-gray-500 italic">
                                            +{staffServices.length - 3} more
                                        </div>
                                    )}
                                </div>
                                {serviceIncentive > 0 && (
                                    <div className="text-xs font-semibold text-purple-600 mt-1">
                                        Incentive: {formatCurrency(serviceIncentive)}
                                    </div>
                                )}
                            </>
                        ) : (
                            <div className="text-xs text-gray-400">No services</div>
                        )}
                    </div>
                );
            }
        },
        {
            title: "Products",
            key: "products",
            align: "left",
            render: (products, row) => {
                const staffProducts = products?.filter(p => getStaffId(p.staff) === selectedStaffId) || [];
                const totalQuantity = staffProducts.reduce((sum, p) => sum + (p.quantity || 1), 0);
                const productIncentive = calculateProductIncentive(products, selectedStaffId);

                return (
                    <div>
                        <div className="font-medium text-green-600 text-sm mb-1">
                            {totalQuantity} Product{totalQuantity !== 1 ? 's' : ''}
                        </div>
                        {staffProducts.length > 0 ? (
                            <>
                                <div className="space-y-0.5">
                                    {staffProducts.slice(0, 3).map((product, index) => (
                                        <div key={index} className="text-xs text-gray-700">
                                            • {product.name} {product.quantity > 1 ? `(x${product.quantity})` : ''}
                                        </div>
                                    ))}
                                    {staffProducts.length > 3 && (
                                        <div className="text-xs text-gray-500 italic">
                                            +{staffProducts.length - 3} more
                                        </div>
                                    )}
                                </div>
                                <div className="text-xs font-semibold text-orange-600 mt-1">
                                    Incentive (5%): {formatCurrency(productIncentive)}
                                </div>
                            </>
                        ) : (
                            <div className="text-xs text-gray-400">No products</div>
                        )}
                    </div>
                );
            }
        },
        {
            title: "Staff Revenue",
            key: "calculations",
            align: "left",
            render: (_, row) => {
                // Calculate revenue from services performed by selected staff only
                let staffRevenue = 0;

                // Check if payment is via wallet
                const isWalletPayment = row.payment?.activePaymentMethods?.some(
                    method => method.method?.toLowerCase() === 'wallet'
                ) || false;

                if (row.services && Array.isArray(row.services)) {
                    row.services.forEach(service => {
                        if (getStaffId(service.staff) === selectedStaffId) {
                            let serviceTotalPrice = service.pricing?.totalPrice || (service.pricing?.finalPrice * (service.quantity || 1)) || 0;

                            // Deduct 30% if payment is via wallet
                            if (isWalletPayment) {
                                serviceTotalPrice = serviceTotalPrice * 0.70; // 70% of original amount
                            }

                            staffRevenue += serviceTotalPrice;
                        }
                    });
                }

                return (
                    <div className="text-left">
                        <div className="text-sm font-bold text-purple-600">
                            {formatCurrency(staffRevenue)}
                        </div>
                        {isWalletPayment && (
                            <div className="text-xs text-orange-600">
                                Wallet (-30%)
                            </div>
                        )}
                    </div>
                );
            }
        },
        {
            title: "Total Incentive",
            key: "incentive",
            align: "right",
            render: (_, row) => {
                const isWalletPayment = row.payment?.activePaymentMethods?.some(
                    method => method.method?.toLowerCase() === 'wallet'
                ) || false;
                const serviceIncentive = calculateStaffIncentive(row.services, selectedStaffId, isWalletPayment);
                const productIncentive = calculateProductIncentive(row.products, selectedStaffId, isWalletPayment);
                const totalIncentive = serviceIncentive + productIncentive;

                return (
                    <div className="text-right">
                        {serviceIncentive > 0 && (
                            <div className="text-xs text-purple-600">
                                Service: {formatCurrency(serviceIncentive)}
                            </div>
                        )}
                        {productIncentive > 0 && (
                            <div className="text-xs text-orange-600">
                                Product: {formatCurrency(productIncentive)}
                            </div>
                        )}
                        <div className="text-sm font-bold text-green-600 mt-0.5 pt-0.5 border-t border-gray-200">
                            {formatCurrency(totalIncentive)}
                        </div>
                    </div>
                );
            }
        },
        {
            title: "Status",
            key: "status",
            align: "center",
            render: (value) => (
                <div className="text-center">
                    {renderBillStatus(value)}
                </div>
            )
        },
        {
            title: "Action",
            key: "action",
            align: "center",
            width: "80px",
            render: (_, row) => (
                <div className="flex justify-center gap-2">
                    <ViewButton
                        onClick={() => handleViewBill(row)}
                        itemName="bill"
                    />
                </div>
            )
        }
    ];

    return (
        <div className='p-3 sm:p-4 md:p-5'>
            <PageHeader
                title={'Staff Reports Management'}
                description={'Manage and view staff reports'}
            />

            {/* Show staff selection only for SuperAdmin */}
            {isSuperAdmin ? (
                <div className="mb-3 sm:mb-4 bg-white rounded-lg shadow p-3 sm:p-4 border border-gray-200">
                    <SearchableSelect
                        title="Select Staff Member"
                        name="userId"
                        options={userOptions}
                        important={true}
                        error={hasError('userId', ApiReducer)}
                        errormsg={getErrorMsg('userId', ApiReducer)}
                        placeholder="Select a staff member to view reports"
                    />
                </div>
            ) : (
                <div className="mb-3 sm:mb-4 bg-white rounded-lg shadow p-3 sm:p-4 border border-gray-200">
                    <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2">
                        <div>
                            <label className="text-xs sm:text-sm font-medium text-gray-700">Staff Member</label>
                            <p className="text-sm sm:text-base font-semibold text-gray-900 mt-1">{selectedStaffName}</p>
                        </div>
                        <div className="px-2.5 sm:px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-xs font-medium whitespace-nowrap">
                            Your Reports
                        </div>
                    </div>
                </div>
            )}

            {selectedStaffId && (activeDateRange || singleDate) && summary && (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-3 sm:gap-4 mb-4 sm:mb-6">
                    <div className="bg-white rounded-lg shadow p-3 sm:p-4 border border-gray-200">
                        <div className="text-xs sm:text-sm text-gray-600 mb-1">Total Bills</div>
                        <div className="text-xl sm:text-2xl font-bold text-gray-900">{summary.totalBills}</div>
                    </div>
                    <div className="bg-white rounded-lg shadow p-3 sm:p-4 border border-gray-200">
                        <div className="text-xs sm:text-sm text-gray-600 mb-1">
                            Salary (Till {summary.salaryInfo.daysInRange}{searchMode === 'single' ? getOrdinalSuffix(summary.salaryInfo.daysInRange) : ' days'}{searchMode === 'single' && summary.salaryInfo.monthName ? ` ${summary.salaryInfo.monthName}` : ''})
                        </div>
                        <div className="text-xl sm:text-2xl font-bold text-indigo-600">₹{Math.round(summary.salaryInfo.proportionalSalary)}</div>
                        {summary.salaryInfo.daysInRange < 30 && (
                            <div className="text-[10px] sm:text-xs text-gray-500 mt-2">
                                Monthly: ₹{Math.round(summary.salaryInfo.monthlySalary)}
                            </div>
                        )}
                    </div>
                    <div className="bg-white rounded-lg shadow p-3 sm:p-4 border border-gray-200">
                        <div className="text-xs sm:text-sm text-gray-600 mb-1">Total Services</div>
                        <div className="text-xl sm:text-2xl font-bold text-blue-600">{summary.totalServices}</div>
                        <div className="mt-2 space-y-0.5">
                            <div className="text-[10px] sm:text-xs text-purple-600 font-medium">
                                Revenue: ₹{Math.round(summary.serviceRevenue)}
                            </div>
                            {summary.totalServiceIncentive > 0 && (
                                <div className="text-[10px] sm:text-xs text-green-600 font-medium">
                                    Incentive: ₹{Math.round(summary.totalServiceIncentive)}
                                </div>
                            )}
                        </div>
                    </div>
                    <div className="bg-white rounded-lg shadow p-3 sm:p-4 border border-gray-200">
                        <div className="text-xs sm:text-sm text-gray-600 mb-1">Total Products</div>
                        <div className="text-xl sm:text-2xl font-bold text-green-600">{summary.totalProducts}</div>
                        <div className="mt-2 space-y-0.5">
                            <div className="text-[10px] sm:text-xs text-purple-600 font-medium">
                                Revenue: ₹{Math.round(summary.productRevenue)}
                            </div>
                            {summary.totalProductIncentive > 0 && (
                                <div className="text-[10px] sm:text-xs text-orange-600 font-medium">
                                    Incentive: ₹{Math.round(summary.totalProductIncentive)}
                                </div>
                            )}
                        </div>
                    </div>
                    <div className="bg-white rounded-lg shadow p-3 sm:p-4 border border-gray-200">
                        <div className="text-xs sm:text-sm text-gray-600 mb-1">Total Incentive</div>
                        <div className="text-xl sm:text-2xl font-bold text-green-600">₹{Math.round(summary.totalIncentive)}</div>
                        <div className="mt-2 space-y-0.5">
                            {summary.totalServiceIncentive > 0 && (
                                <div className="text-[10px] sm:text-xs text-purple-600 font-medium">
                                    Service: ₹{Math.round(summary.totalServiceIncentive)}
                                </div>
                            )}
                            {summary.totalProductIncentive > 0 && (
                                <div className="text-[10px] sm:text-xs text-orange-600 font-medium">
                                    Product: ₹{Math.round(summary.totalProductIncentive)}
                                </div>
                            )}
                        </div>
                        {summary.targetAchievement.percentage >= 100 ? (
                            <div className="mt-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                                </svg>
                                Eligible
                            </div>
                        ) : (
                            <div className="mt-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                                <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                                </svg>
                                Not Eligible
                            </div>
                        )}
                    </div>
                    <div className="bg-white rounded-lg shadow p-3 sm:p-4 border border-gray-200">
                        <div className="text-xs sm:text-sm text-gray-600 mb-1">Target Achievement ({summary.targetAchievement.daysInRange} days)</div>
                        <div className={`text-xl sm:text-2xl font-bold ${summary.targetAchievement.percentage >= 100
                            ? 'text-green-600'
                            : summary.targetAchievement.percentage >= 70
                                ? 'text-orange-600'
                                : 'text-red-600'
                            }`}>
                            {summary.targetAchievement.percentage.toFixed(1)}%
                        </div>
                        <div className="text-[10px] sm:text-xs text-gray-500 mt-1">
                            {formatCurrency(summary.targetAchievement.achieved)} / {formatCurrency(summary.targetAchievement.target)}
                        </div>
                        <div className="text-[10px] sm:text-xs text-gray-400 mt-1">
                            Per Day: {formatCurrency(summary.targetAchievement.perDayTarget)}
                        </div>
                    </div>
                </div>
            )}

            {selectedStaffId && !activeDateRange && !singleDate && (
                <div className="bg-white rounded-lg shadow p-4 sm:p-6 md:p-8 border border-gray-200">
                    <div className="text-center mb-4 sm:mb-6">
                        <div className="text-gray-500 text-base sm:text-lg mb-2">Select Date</div>
                        <div className="text-gray-400 text-sm mb-2 sm:mb-4 px-2">Please select a date to view reports for {selectedStaffName}</div>
                    </div>

                    {/* Search Mode Toggle */}
                    <div className="flex justify-center mb-4 sm:mb-6">
                        <div className="inline-flex rounded-lg border border-gray-300 p-1 bg-gray-50 w-full sm:w-auto">
                            <button
                                onClick={() => {
                                    setSearchMode('single');
                                    setActiveDateRange(null);
                                    setSingleDate(new Date().toISOString().split('T')[0]); // Set today's date
                                }}
                                className={`flex-1 sm:flex-none px-4 sm:px-6 py-2 rounded-md text-xs sm:text-sm font-medium transition-all ${searchMode === 'single'
                                        ? 'bg-gradient-to-r from-purple-600 to-blue-600 text-white shadow-md'
                                        : 'text-gray-600 hover:text-gray-900'
                                    }`}
                            >
                                Single Date
                            </button>
                            <button
                                onClick={() => {
                                    setSearchMode('range');
                                    setSingleDate(new Date().toISOString().split('T')[0]); // Keep today's date for when switching back
                                }}
                                className={`flex-1 sm:flex-none px-4 sm:px-6 py-2 rounded-md text-xs sm:text-sm font-medium transition-all ${searchMode === 'range'
                                        ? 'bg-gradient-to-r from-purple-600 to-blue-600 text-white shadow-md'
                                        : 'text-gray-600 hover:text-gray-900'
                                    }`}
                            >
                                Date Range
                            </button>
                        </div>
                    </div>

                    {/* Single Date Search */}
                    {searchMode === 'single' && (
                        <div className="flex flex-col sm:flex-row justify-center items-stretch sm:items-center gap-3">
                            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
                                <label className="text-xs sm:text-sm font-medium text-gray-700">Select Date:</label>
                                <input
                                    type="date"
                                    value={singleDate}
                                    onChange={(e) => setSingleDate(e.target.value)}
                                    max={new Date().toISOString().split('T')[0]}
                                    className="block w-full sm:w-auto px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-xs sm:text-sm"
                                />
                            </div>
                            <button
                                onClick={handleSingleDateSearch}
                                disabled={!singleDate}
                                className="w-full sm:w-auto px-4 sm:px-6 py-2 bg-gradient-to-r from-purple-600 to-blue-600 text-white rounded-lg hover:from-purple-700 hover:to-blue-700 transition-all shadow-md disabled:opacity-50 disabled:cursor-not-allowed text-xs sm:text-sm font-medium"
                            >
                                Search
                            </button>
                        </div>
                    )}

                    {/* Date Range Search */}
                    {searchMode === 'range' && (
                        <div>
                            <div className="text-center text-gray-400 text-xs sm:text-sm mb-4">(Maximum 90 days)</div>
                            <div className="flex justify-center px-2 sm:px-0">
                                <DateSearch
                                    onSearch={handleDateRangeSearch}
                                    onClear={handleClearSearch}
                                />
                            </div>
                        </div>
                    )}
                </div>
            )}

            {selectedStaffId && (activeDateRange || singleDate) && (
                <AppTable
                    TH={tableHeaders}
                    TD={billsData}
                    isLoading={isLoading}
                    emptyMessage={`No bills found for ${selectedStaffName || 'selected staff'} ${searchMode === 'single' ? 'on the selected date' : 'in the selected date range'}`}
                    enableExport={true}
                    onExportData={handleExportData}
                    exportFileName={`staff_report_${selectedStaffName}`}
                    hidePagination={true}
                    TableSearch={
                        <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 items-stretch sm:items-center">
                            {/* Search Mode Toggle */}
                            <div className="inline-flex rounded-lg border border-gray-300 p-1 bg-gray-50 w-full sm:w-auto">
                                <button
                                    onClick={() => {
                                        setSearchMode('single');
                                        setActiveDateRange(null);
                                        setSingleDate(new Date().toISOString().split('T')[0]); // Set today's date
                                        setBillsData([]);
                                    }}
                                    className={`flex-1 sm:flex-none px-3 sm:px-4 py-1.5 rounded-md text-[10px] sm:text-xs font-medium transition-all ${searchMode === 'single'
                                            ? 'bg-gradient-to-r from-purple-600 to-blue-600 text-white shadow-md'
                                            : 'text-gray-600 hover:text-gray-900'
                                        }`}
                                >
                                    Single Date
                                </button>
                                <button
                                    onClick={() => {
                                        setSearchMode('range');
                                        setSingleDate(new Date().toISOString().split('T')[0]); // Keep today's date
                                        setBillsData([]);
                                    }}
                                    className={`flex-1 sm:flex-none px-3 sm:px-4 py-1.5 rounded-md text-[10px] sm:text-xs font-medium transition-all ${searchMode === 'range'
                                            ? 'bg-gradient-to-r from-purple-600 to-blue-600 text-white shadow-md'
                                            : 'text-gray-600 hover:text-gray-900'
                                        }`}
                                >
                                    Date Range
                                </button>
                            </div>

                            {/* Single Date Search */}
                            {searchMode === 'single' && (
                                <div className="flex items-center gap-2 flex-1 sm:flex-none">
                                    <input
                                        type="date"
                                        value={singleDate}
                                        onChange={(e) => setSingleDate(e.target.value)}
                                        max={new Date().toISOString().split('T')[0]}
                                        className="block flex-1 sm:flex-none px-2 sm:px-3 py-1.5 sm:py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-xs sm:text-sm"
                                    />
                                    <button
                                        onClick={handleSingleDateSearch}
                                        disabled={!singleDate}
                                        className="px-3 sm:px-4 py-1.5 sm:py-2 bg-gradient-to-r from-purple-600 to-blue-600 text-white rounded-lg hover:from-purple-700 hover:to-blue-700 transition-all shadow-md disabled:opacity-50 disabled:cursor-not-allowed text-xs sm:text-sm font-medium whitespace-nowrap"
                                    >
                                        Search
                                    </button>
                                </div>
                            )}

                            {/* Date Range Search */}
                            {searchMode === 'range' && (
                                <div className="flex-1 sm:flex-none">
                                    <DateSearch
                                        onSearch={handleDateRangeSearch}
                                        onClear={handleClearSearch}
                                    />
                                </div>
                            )}

                            <button
                                onClick={handleClearSearch}
                                className="p-2 bg-gradient-to-r from-purple-600 to-blue-600 text-white rounded-lg hover:from-purple-700 hover:to-blue-700 transition-all shadow-md self-center sm:self-auto"
                                title="Clear Search"
                            >
                                <XMarkIcon className="w-4 h-4 sm:w-5 sm:h-5" />
                            </button>
                        </div>
                    }
                />
            )}

            {!selectedStaffId && isSuperAdmin && (
                <div className="bg-white rounded-lg shadow p-6 sm:p-8 text-center border border-gray-200">
                    <div className="text-gray-500 text-base sm:text-lg mb-2">No Staff Selected</div>
                    <div className="text-gray-400 text-sm sm:text-base px-4">Please select a staff member to view their reports</div>
                </div>
            )}

            {/* Bill Details Modal */}
            <CustomModal
                isOpen={isModalOpen}
                onClose={handleCloseModal}
                size="7xl"
            >
                {selectedBill && (
                    <div className="p-3 sm:p-4">
                        {/* Modal Header */}
                        <div className="flex justify-between items-start sm:items-center mb-3 sm:mb-4 border-b pb-2 sm:pb-3">
                            <div className="flex-1 pr-2">
                                <h2 className="text-base sm:text-xl font-bold text-gray-900 break-words">
                                    Bill Details - {selectedBill.billNumber || 'N/A'}
                                </h2>
                                <p className="text-[10px] sm:text-xs text-gray-500 mt-0.5">
                                    View complete details of bill #{selectedBill.billNumber || 'N/A'}
                                </p>
                            </div>
                            <button
                                onClick={handleCloseModal}
                                className="text-gray-400 hover:text-gray-600 transition-colors p-1 sm:p-1.5 hover:bg-gray-100 rounded-lg flex-shrink-0"
                            >
                                <XMarkIcon className="h-4 w-4 sm:h-5 sm:w-5" />
                            </button>
                        </div>

                        {/* Modal Content */}
                        <div className="grid grid-cols-1 lg:grid-cols-3 gap-3 sm:gap-4 max-h-[70vh] sm:max-h-[75vh] overflow-y-auto">
                            {/* Left Column - Main Details */}
                            <div className="lg:col-span-2 space-y-3">
                                {/* Bill Information */}
                                <div className="bg-gray-50 rounded-lg border border-gray-200 p-2 sm:p-3">
                                    <h3 className="text-xs sm:text-sm font-semibold text-gray-900 mb-2">Bill Information</h3>
                                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 text-[10px] sm:text-xs">
                                        <div>
                                            <p className="text-gray-600">Bill Number</p>
                                            <p className="font-medium text-gray-900">{selectedBill.billNumber || 'N/A'}</p>
                                        </div>
                                        <div>
                                            <p className="text-gray-600">Transaction ID</p>
                                            <p className="font-medium text-gray-900 truncate" title={selectedBill.transactionId}>{selectedBill.transactionId || 'N/A'}</p>
                                        </div>
                                        <div>
                                            <p className="text-gray-600">Date & Time</p>
                                            <p className="font-medium text-gray-900">{formatDate(selectedBill.timestamp)} {formatTime(selectedBill.timestamp)}</p>
                                        </div>
                                        <div>
                                            <p className="text-gray-600">Bill Status</p>
                                            <div className="mt-0.5">{renderBillStatus(selectedBill.status)}</div>
                                        </div>
                                        <div>
                                            <p className="text-gray-600">Bill Type</p>
                                            <p className="font-medium text-gray-900">
                                                {selectedBill.billType ? selectedBill.billType.replace(/_/g, ' ') : 'N/A'}
                                            </p>
                                        </div>
                                        {selectedBill.tax?.taxMessage && (
                                            <div>
                                                <p className="text-gray-600">Tax Info</p>
                                                <p className="font-medium text-gray-900">{selectedBill.tax.taxMessage}</p>
                                            </div>
                                        )}
                                    </div>
                                </div>

                                {/* Client Information */}
                                <div className="bg-gray-50 rounded-lg border border-gray-200 p-2 sm:p-3">
                                    <h3 className="text-xs sm:text-sm font-semibold text-gray-900 mb-2">Client Information</h3>
                                    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2 text-[10px] sm:text-xs">
                                        <div>
                                            <p className="text-gray-600">Name</p>
                                            <p className="font-medium text-gray-900">{selectedBill.client?.name || 'N/A'}</p>
                                        </div>
                                        <div>
                                            <p className="text-gray-600">Phone Number</p>
                                            <p className="font-medium text-gray-900">{selectedBill.client?.phoneNumber || 'N/A'}</p>
                                        </div>
                                        <div>
                                            <p className="text-gray-600">Gender</p>
                                            <p className="font-medium text-gray-900">{selectedBill.client?.gender || 'N/A'}</p>
                                        </div>
                                        <div>
                                            <p className="text-gray-600">Age Group</p>
                                            <p className="font-medium text-gray-900">{selectedBill.client?.ageGroup || 'N/A'}</p>
                                        </div>
                                        <div>
                                            <p className="text-gray-600">Customer Type</p>
                                            <p className="font-medium text-gray-900">{selectedBill.client?.customerType || 'N/A'}</p>
                                        </div>
                                        <div>
                                            <p className="text-gray-600">Client Type</p>
                                            <p className="font-medium text-gray-900">{selectedBill.client?.clientType || 'N/A'}</p>
                                        </div>
                                        <div>
                                            <p className="text-gray-600">Total Visits</p>
                                            <p className="font-medium text-gray-900">{selectedBill.client?.totalVisit || 0}</p>
                                        </div>
                                        <div>
                                            <p className="text-gray-600">Wallet Balance</p>
                                            <p className="font-medium text-gray-900">
                                                {formatCurrency(selectedBill.client?.walletId?.balance || 0)}
                                            </p>
                                        </div>
                                    </div>
                                </div>

                                {/* Services */}
                                {selectedBill.services && selectedBill.services.length > 0 && (
                                    <div className="bg-gray-50 rounded-lg border border-gray-200 p-2 sm:p-3">
                                        <h3 className="text-xs sm:text-sm font-semibold text-gray-900 mb-2">
                                            Services ({selectedBill.services.length})
                                        </h3>
                                        <div className="space-y-2 max-h-40 sm:max-h-48 overflow-y-auto">
                                            {selectedBill.services.map((service, index) => (
                                                <div key={index} className="bg-white border border-gray-200 rounded p-2 text-xs">
                                                    <div className="flex justify-between items-start">
                                                        <div className="flex-1">
                                                            <p className="font-medium text-gray-900">{service.name}</p>
                                                            <div className="flex gap-2 items-center">
                                                                <p className="text-gray-600">Qty: {service.quantity || 1}</p>
                                                                {service.pricing?.savings > 0 && (
                                                                    <span className="text-green-600 text-[10px]">
                                                                        (Saved: {formatCurrency(service.pricing.savings)})
                                                                    </span>
                                                                )}
                                                            </div>
                                                            {service.staff && (() => {
                                                                const staffId = typeof service.staff === 'object' ? service.staff._id : service.staff;
                                                                const staff = staffDetailsMap[staffId];

                                                                if (staff) {
                                                                    return (
                                                                        <div className="mt-1 flex items-center gap-1">
                                                                            <svg className="w-3 h-3 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                                                                            </svg>
                                                                            <span className="text-[10px] font-medium text-blue-600">
                                                                                Performed by: {staff.name}
                                                                            </span>
                                                                        </div>
                                                                    );
                                                                }
                                                                return null;
                                                            })()}
                                                        </div>
                                                        <div className="text-right">
                                                            <p className="font-semibold text-gray-900">
                                                                {formatCurrency(service.pricing?.finalPrice || service.pricing?.totalPrice || 0)}
                                                            </p>
                                                            {service.pricing?.basePrice !== service.pricing?.finalPrice && (
                                                                <p className="text-gray-500 line-through text-[10px]">
                                                                    {formatCurrency(service.pricing?.basePrice || 0)}
                                                                </p>
                                                            )}
                                                        </div>
                                                    </div>
                                                    {service.incentive > 0 && (() => {
                                                        const isWallet = selectedBill.payment?.activePaymentMethods?.some(
                                                            m => m.method?.toLowerCase() === 'wallet'
                                                        ) || false;
                                                        const basePrice = service.pricing?.finalPrice || 0;
                                                        const adjustedPrice = isWallet ? basePrice * 0.70 : basePrice;
                                                        return (
                                                            <div className="mt-1 flex justify-between items-center border-t border-gray-100 pt-1">
                                                                <span className="text-green-600">Incentive: {service.incentive}%</span>
                                                                <span className="text-green-600 font-medium">
                                                                    {formatCurrency(adjustedPrice * (service.incentive / 100))}
                                                                </span>
                                                            </div>
                                                        );
                                                    })()}
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                {/* Products */}
                                {selectedBill.products && selectedBill.products.length > 0 && (
                                    <div className="bg-gray-50 rounded-lg border border-gray-200 p-2 sm:p-3">
                                        <h3 className="text-xs sm:text-sm font-semibold text-gray-900 mb-2">
                                            Products ({selectedBill.products.length})
                                        </h3>
                                        <div className="space-y-2 max-h-40 sm:max-h-48 overflow-y-auto">
                                            {selectedBill.products.map((product, index) => (
                                                <div key={index} className="bg-white border border-gray-200 rounded p-2 text-xs">
                                                    <div className="flex justify-between items-start">
                                                        <div className="flex-1">
                                                            <p className="font-medium text-gray-900">{product.name}</p>
                                                            <div className="flex gap-2 items-center">
                                                                <p className="text-gray-600">Qty: {product.quantity || 1}</p>
                                                                {product.pricing?.savings > 0 && (
                                                                    <span className="text-green-600 text-[10px]">
                                                                        (Saved: {formatCurrency(product.pricing.savings)})
                                                                    </span>
                                                                )}
                                                            </div>
                                                            {product.staff && (() => {
                                                                const staffId = typeof product.staff === 'object' ? product.staff._id : product.staff;
                                                                const staff = staffDetailsMap[staffId];

                                                                if (staff) {
                                                                    return (
                                                                        <div className="mt-1 flex items-center gap-1">
                                                                            <svg className="w-3 h-3 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                                                                            </svg>
                                                                            <span className="text-[10px] font-medium text-green-600">
                                                                                Sold by: {staff.name}
                                                                            </span>
                                                                        </div>
                                                                    );
                                                                }
                                                                return null;
                                                            })()}
                                                        </div>
                                                        <div className="text-right">
                                                            <p className="font-semibold text-gray-900">
                                                                {formatCurrency(product.pricing?.finalPrice || product.pricing?.totalPrice || 0)}
                                                            </p>
                                                            {product.pricing?.basePrice !== product.pricing?.finalPrice && (
                                                                <p className="text-gray-500 line-through text-[10px]">
                                                                    {formatCurrency(product.pricing?.basePrice || 0)}
                                                                </p>
                                                            )}
                                                        </div>
                                                    </div>
                                                    {(() => {
                                                        const isWallet = selectedBill.payment?.activePaymentMethods?.some(
                                                            m => m.method?.toLowerCase() === 'wallet'
                                                        ) || false;
                                                        let productTotal = product.pricing?.finalPrice || product.pricing?.totalPrice || 0;
                                                        if (isWallet) {
                                                            productTotal = productTotal * 0.70;
                                                        }
                                                        const incentivePercentage = product.incentive || 5;
                                                        const incentiveAmount = (productTotal * incentivePercentage) / 100;

                                                        if (incentiveAmount > 0) {
                                                            return (
                                                                <div className="mt-1 flex justify-between items-center border-t border-gray-100 pt-1">
                                                                    <span className="text-orange-600">Incentive: {incentivePercentage}%</span>
                                                                    <span className="text-orange-600 font-medium">
                                                                        {formatCurrency(incentiveAmount)}
                                                                    </span>
                                                                </div>
                                                            );
                                                        }
                                                        return null;
                                                    })()}
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                {/* Memberships */}
                                {selectedBill.newMemberships && selectedBill.newMemberships.length > 0 && (
                                    <div className="bg-gray-50 rounded-lg border border-gray-200 p-2 sm:p-3">
                                        <h3 className="text-xs sm:text-sm font-semibold text-gray-900 mb-2">
                                            Memberships ({selectedBill.newMemberships.length})
                                        </h3>
                                        <div className="space-y-2">
                                            {selectedBill.newMemberships.map((membership, index) => (
                                                <div key={index} className="bg-white border border-gray-200 rounded p-2 text-xs">
                                                    <div className="flex justify-between items-start">
                                                        <div className="flex-1">
                                                            <p className="font-medium text-gray-900">{membership.name}</p>
                                                            <div className="flex gap-2 items-center">
                                                                <p className="text-gray-600">Duration: {membership.duration || 'N/A'}</p>
                                                                {membership.pricing?.savings > 0 && (
                                                                    <span className="text-green-600 text-[10px]">
                                                                        (Saved: {formatCurrency(membership.pricing.savings)})
                                                                    </span>
                                                                )}
                                                            </div>
                                                        </div>
                                                        <div className="text-right">
                                                            <p className="font-semibold text-gray-900">
                                                                {formatCurrency(membership.pricing?.finalPrice || membership.pricing?.totalPrice || 0)}
                                                            </p>
                                                            {membership.pricing?.basePrice !== membership.pricing?.finalPrice && (
                                                                <p className="text-gray-500 line-through text-[10px]">
                                                                    {formatCurrency(membership.pricing?.basePrice || 0)}
                                                                </p>
                                                            )}
                                                        </div>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </div>

                            {/* Right Column - Payment & Totals */}
                            <div className="space-y-2 sm:space-y-3">
                                {/* Items Breakdown */}
                                {selectedBill.calculations?.items && (
                                    <div className="bg-blue-50 rounded-lg border border-blue-200 p-2 sm:p-3">
                                        <h3 className="text-xs sm:text-sm font-semibold text-blue-900 mb-2">📊 Items Breakdown</h3>
                                        <div className="space-y-1.5 text-[10px] sm:text-xs">
                                            {selectedBill.calculations.items.services?.count > 0 && (
                                                <div className="flex justify-between items-center">
                                                    <span className="text-blue-700">Services ({selectedBill.calculations.items.services.count})</span>
                                                    <span className="text-blue-700 font-medium">
                                                        {formatCurrency(selectedBill.calculations.items.services.finalTotal || 0)}
                                                    </span>
                                                </div>
                                            )}
                                            {selectedBill.calculations.items.products?.count > 0 && (
                                                <div className="flex justify-between items-center">
                                                    <span className="text-blue-700">Products ({selectedBill.calculations.items.products.count})</span>
                                                    <span className="text-blue-700 font-medium">
                                                        {formatCurrency(selectedBill.calculations.items.products.finalTotal || 0)}
                                                    </span>
                                                </div>
                                            )}
                                            {selectedBill.calculations.items.memberships?.count > 0 && (
                                                <div className="flex justify-between items-center">
                                                    <span className="text-blue-700">Memberships ({selectedBill.calculations.items.memberships.count})</span>
                                                    <span className="text-blue-700 font-medium">
                                                        {formatCurrency(selectedBill.calculations.items.memberships.finalTotal || 0)}
                                                    </span>
                                                </div>
                                            )}
                                            <div className="pt-1.5 border-t border-blue-200 flex justify-between items-center">
                                                <span className="text-blue-800 font-semibold">Total Items ({selectedBill.calculations.totals?.totalItems || 0})</span>
                                                <span className="text-blue-800 font-bold">
                                                    {formatCurrency(selectedBill.calculations.totals?.subtotalAfterItemDiscount || 0)}
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {/* Payment Information */}
                                <div className="bg-gray-50 rounded-lg border border-gray-200 p-2 sm:p-3">
                                    <h3 className="text-xs sm:text-sm font-semibold text-gray-900 mb-2">Payment</h3>
                                    <div className="space-y-2 text-[10px] sm:text-xs">
                                        <div>
                                            <p className="text-gray-600 mb-0.5">Status</p>
                                            {renderPaymentStatus(selectedBill.payment)}
                                        </div>
                                        {selectedBill.payment?.activePaymentMethods && selectedBill.payment.activePaymentMethods.length > 0 && (
                                            <div>
                                                <p className="text-gray-600 mb-1">Payment Methods</p>
                                                <div className="space-y-1">
                                                    {selectedBill.payment.activePaymentMethods.map((method, index) => (
                                                        <div key={index} className="flex justify-between items-center bg-white p-1.5 rounded">
                                                            <span className="font-medium text-gray-700">{method.method}</span>
                                                            <span className="text-gray-900">{formatCurrency(method.amount)}</span>
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        )}
                                        <div className="pt-2 border-t border-gray-200">
                                            <div className="flex justify-between items-center">
                                                <span className="font-medium text-gray-700">Total Paid</span>
                                                <span className="font-bold text-sm text-gray-900">
                                                    {formatCurrency(selectedBill.payment?.totalPaid || 0)}
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* Discount Breakdown */}
                                {selectedBill.discounts?.hasDiscounts && (
                                    <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-lg border border-green-200 p-2 sm:p-3">
                                        <h3 className="text-xs sm:text-sm font-semibold text-green-900 mb-2">💰 Discount Breakdown</h3>
                                        <div className="space-y-1.5 text-[10px] sm:text-xs">
                                            {selectedBill.discounts?.membershipDiscount?.applied && (
                                                <div className="flex justify-between items-center">
                                                    <span className="text-green-700">Membership Discount</span>
                                                    <span className="text-green-700 font-bold">
                                                        -{formatCurrency(selectedBill.discounts.membershipDiscount.amount || 0)}
                                                    </span>
                                                </div>
                                            )}
                                            {selectedBill.discounts?.couponDiscount?.applied && (
                                                <div className="flex justify-between items-center">
                                                    <span className="text-green-700">Coupon ({selectedBill.discounts.couponDiscount.code})</span>
                                                    <span className="text-green-700 font-bold">
                                                        -{formatCurrency(selectedBill.discounts.couponDiscount.amount || 0)}
                                                    </span>
                                                </div>
                                            )}
                                            {selectedBill.discounts?.walletUsed && (
                                                <div className="flex justify-between items-center">
                                                    <span className="text-green-700">Wallet Used</span>
                                                    <span className="text-green-700 font-bold">✓</span>
                                                </div>
                                            )}
                                            <div className="pt-1.5 border-t border-green-200 flex justify-between items-center">
                                                <span className="text-green-800 font-semibold">Total Discount</span>
                                                <span className="text-green-800 font-bold">
                                                    -{formatCurrency(selectedBill.discounts.totalDiscountAmount || 0)}
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {/* Bill Summary */}
                                <div className="bg-gray-50 rounded-lg border border-gray-200 p-2 sm:p-3">
                                    <h3 className="text-xs sm:text-sm font-semibold text-gray-900 mb-2">Bill Summary</h3>
                                    <div className="space-y-1.5 text-[10px] sm:text-xs">
                                        <div className="flex justify-between items-center">
                                            <span className="text-gray-600">Subtotal (Before Discount)</span>
                                            <span className="text-gray-900">
                                                {formatCurrency(selectedBill.calculations?.totals?.subtotalBeforeDiscount || 0)}
                                            </span>
                                        </div>
                                        {selectedBill.calculations?.totals?.totalItemDiscount > 0 && (
                                            <div className="flex justify-between items-center text-green-600">
                                                <span>Item Discount</span>
                                                <span>-{formatCurrency(selectedBill.calculations?.totals?.totalItemDiscount || 0)}</span>
                                            </div>
                                        )}
                                        {selectedBill.calculations?.totals?.couponDiscount > 0 && (
                                            <div className="flex justify-between items-center text-purple-600">
                                                <span>Coupon Discount</span>
                                                <span>-{formatCurrency(selectedBill.calculations?.totals?.couponDiscount || 0)}</span>
                                            </div>
                                        )}
                                        <div className="flex justify-between items-center">
                                            <span className="text-gray-600">After Discount</span>
                                            <span className="text-gray-900">
                                                {formatCurrency(selectedBill.calculations?.totals?.subtotalAfterCoupon || 0)}
                                            </span>
                                        </div>
                                        {selectedBill.calculations?.totals?.gstAmount > 0 && (
                                            <div className="flex justify-between items-center">
                                                <span className="text-gray-600">
                                                    GST {selectedBill.tax?.taxIncluded ? '(Included)' : ''} ({selectedBill.calculations?.totals?.gstPercentage || 0}%)
                                                </span>
                                                <span className="text-gray-900">
                                                    {formatCurrency(selectedBill.calculations?.totals?.gstAmount || 0)}
                                                </span>
                                            </div>
                                        )}
                                        {selectedBill.calculations?.totals?.roundOffAmount !== 0 && (
                                            <div className="flex justify-between items-center">
                                                <span className="text-gray-600">Round Off</span>
                                                <span className="text-gray-900">
                                                    {formatCurrency(selectedBill.calculations?.totals?.roundOffAmount || 0)}
                                                </span>
                                            </div>
                                        )}
                                        <div className="pt-2 border-t-2 border-gray-300">
                                            <div className="flex justify-between items-center">
                                                <span className="text-xs sm:text-sm font-semibold text-gray-900">Grand Total</span>
                                                <span className="text-sm sm:text-base font-bold text-gray-900">
                                                    {formatCurrency(selectedBill.calculations?.totals?.finalAmount || selectedBill.calculations?.totals?.grandTotal || 0)}
                                                </span>
                                            </div>
                                        </div>
                                        {selectedBill.calculations?.totals?.totalSavings > 0 && (
                                            <div className="bg-green-50 p-1.5 rounded">
                                                <div className="flex justify-between items-center">
                                                    <span className="text-green-700 font-medium">🎉 You Saved</span>
                                                    <span className="text-green-700 font-bold">
                                                        {formatCurrency(selectedBill.calculations?.totals?.totalSavings || 0)}
                                                    </span>
                                                </div>
                                            </div>
                                        )}
                                        {selectedBill.changeReturned > 0 && (
                                            <div className="bg-blue-50 p-1.5 rounded mt-2">
                                                <div className="flex justify-between items-center">
                                                    <span className="text-blue-700 font-medium">Change Returned</span>
                                                    <span className="text-blue-700 font-bold">
                                                        {formatCurrency(selectedBill.changeReturned || 0)}
                                                    </span>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>

                                {/* Additional Info */}
                                {selectedBill.remarks && (
                                    <div className="bg-gray-50 rounded-lg border border-gray-200 p-2 sm:p-3">
                                        <h3 className="text-xs sm:text-sm font-semibold text-gray-900 mb-1">Remarks</h3>
                                        <p className="text-[10px] sm:text-xs text-gray-700">{selectedBill.remarks}</p>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                )}
            </CustomModal>
        </div>
    )
}

export default StaffReportMaster